import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Card,
    Button,
    Breadcrumb,
    Table,
    Pagination,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getAllLeaveCycles,
    createLeaveCycle,
    updateLeaveCycle,
} from "../../../../api/onboarddeboard";

const MONTHS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const ITEMS_PER_PAGE = 20;

const LeaveCycle = () => {
    const token = localStorage.getItem("token");

    const [cycleName, setCycleName] = useState("");
    const [fromMonth, setFromMonth] = useState("");
    const [toMonth, setToMonth] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [errors, setErrors] = useState({});

    const [editingId, setEditingId] = useState(null);

    const [leaveCycles, setLeaveCycles] = useState([]);
    const [filterActive, setFilterActive] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOption, setSortOption] = useState("latest");

    const fetchLeaveCycles = useCallback(async () => {
        try {
            const response = await getAllLeaveCycles(token);
            const sorted = (response.data || []).sort((a, b) => b.id - a.id);
            setLeaveCycles(sorted);
        } catch (error) {
            toast.error("Failed to fetch leave cycles.");
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchLeaveCycles();
    }, [token, fetchLeaveCycles]);

    // SAME VALIDATION LOGIC USED FOR:
    // 1. While typing
    // 2. On Submit
    const getCycleNameError = (value) => {
        const trimmedValue = value.trim();

        if (/^\s+$/.test(value)) {
            return "Cycle Name cannot be only spaces.";
        }

        if (!trimmedValue) {
            return "Cycle Name is required.";
        }

        if (/^\d+$/.test(trimmedValue)) {
            return "Cycle Name cannot be only numeric.";
        }

        if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
            return "Cycle Name cannot be only special characters.";
        }

        if (
            /^[^a-zA-Z]*$/.test(trimmedValue) &&
            /[0-9]/.test(trimmedValue) &&
            /[^a-zA-Z0-9]/.test(trimmedValue)
        ) {
            return "Cycle Name cannot be only numbers and special characters.";
        }

        if (trimmedValue.length > 100) {
            return "Cycle Name cannot exceed 100 characters.";
        }

        const duplicate = leaveCycles.some(
            (cycle) =>
                cycle.cycleName.toLowerCase() ===
                    trimmedValue.toLowerCase() &&
                cycle.id !== editingId
        );

        if (duplicate) {
            return "A Leave Cycle with this name already exists.";
        }

        return "";
    };

    const handleCycleNameChange = (e) => {
        const value = e.target.value;

        setCycleName(value);

        setErrors((prev) => ({
            ...prev,
            cycleName: value ? getCycleNameError(value) : "",
        }));
    };

    const validate = () => {
        const newErrors = {};

        // SAME VALIDATION FUNCTION USED ON SUBMIT
        const cycleNameError = getCycleNameError(cycleName);

        if (cycleNameError) {
            newErrors.cycleName = cycleNameError;
        }

        if (!fromMonth) {
            newErrors.fromMonth = "From Month is required";
        }

        if (!toMonth) {
            newErrors.toMonth = "To Month is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        const payload = {
            cycleName: cycleName.trim(),
            fromMonth: Number(fromMonth),
            toMonth: Number(toMonth),
            isActive,
        };

        try {
            if (editingId) {
                await updateLeaveCycle(editingId, payload, token);
                await fetchLeaveCycles();
                resetForm();
                toast.success("Leave cycle updated successfully");
            } else {
                await createLeaveCycle(payload, token);
                await fetchLeaveCycles();
                resetForm();
                toast.success("Leave cycle created successfully");
            }
        } catch (error) {
            const data = error?.response?.data;

            const message =
                typeof data === "string"
                    ? data
                    : data?.message ||
                      data?.error ||
                      "Failed to save leave cycle";

            toast.error(message);
        }
    };

    const handleEdit = (cycle) => {
        setEditingId(cycle.id);
        setCycleName(cycle.cycleName);
        setFromMonth(String(cycle.fromMonth));
        setToMonth(String(cycle.toMonth));
        setIsActive(cycle.isActive);
        setErrors({});

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const resetForm = () => {
        setCycleName("");
        setFromMonth("");
        setToMonth("");
        setIsActive(true);
        setEditingId(null);
        setErrors({});
    };

    const handleCancel = () => resetForm();

    const displayedCycles = leaveCycles
        .filter((cycle) => {
            const matchesSearch = cycle.cycleName
                .toLowerCase()
                .startsWith(searchTerm.toLowerCase().trim());

            const matchesStatus =
                filterActive === ""
                    ? true
                    : cycle.isActive === (filterActive === "true");

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (sortOption === "nameAZ") {
                return a.cycleName.localeCompare(b.cycleName);
            }

            return b.id - a.id;
        });

    const totalPages = Math.ceil(
        displayedCycles.length / ITEMS_PER_PAGE
    );

    const paginated = displayedCycles.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const getMonthLabel = (value) =>
        MONTHS.find((m) => m.value === value)?.label || value;

    const exportToCSV = () => {
        if (leaveCycles.length === 0) {
            toast.error("No data available to export");
            return;
        }

        const headers = [
            "#",
            "Cycle Name",
            "From Month",
            "To Month",
            "Status",
        ];

        const rows = leaveCycles.map((cycle, index) => [
            index + 1,
            cycle.cycleName,
            getMonthLabel(cycle.fromMonth),
            getMonthLabel(cycle.toMonth),
            cycle.isActive ? "Active" : "Inactive",
        ]);

        const csvContent = [headers, ...rows]
            .map((row) => row.join(","))
            .join("\n");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute(
            "download",
            "leave_cycles.csv"
        );

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <Container fluid className="p-4">
            <style>{`
                .leave-toggle .form-check-input:checked {
                    background-color: #2E5CB8 !important;
                    border-color: #2E5CB8 !important;
                }

                .leave-toggle .form-check-input:focus {
                    border-color: #2E5CB8 !important;
                    box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important;
                }

                .lc-breadcrumb a {
                    text-decoration: none !important;
                    color: #2E5CB8 !important;
                }

                .lc-breadcrumb .breadcrumb-item.active {
                    color: #6c757d !important;
                }

                .pagination .page-item.active .page-link {
                    background-color: #2E5CB8 !important;
                    border-color: #2E5CB8 !important;
                }
            `}</style>

           
<h4
    className="page-main-heading"
    style={{ marginBottom: 12 }}
>
    Leave Cycle
</h4>

            <ToastContainer
                position="top-right"
                autoClose={3000}
            />

            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">
                    Leave
                </Breadcrumb.Item>

                <Breadcrumb.Item href="#">
                    Master
                </Breadcrumb.Item>

                <Breadcrumb.Item active>
                    Leave Cycle
                </Breadcrumb.Item>
            </Breadcrumb>

            <Row className="mb-4">
                <Col
                    xl={12}
                    lg={12}
                    md={12}
                    xs={12}
                >
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Cycle Name
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </Form.Label>

                                            <Col sm={8}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Cycle Name"
                                                    value={cycleName}
                                                    maxLength={100}
                                                    onChange={
                                                        handleCycleNameChange
                                                    }
                                                />

                                                {errors.cycleName && (
                                                    <p
                                                        style={{
                                                            color: "red",
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {errors.cycleName}
                                                    </p>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                From Month
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </Form.Label>

                                            <Col sm={8}>
                                                <Form.Select
                                                    value={fromMonth}
                                                    onChange={(e) => {
                                                        setFromMonth(
                                                            e.target.value
                                                        );

                                                        setErrors((prev) => ({
                                                            ...prev,
                                                            fromMonth: "",
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select Month
                                                    </option>

                                                    {MONTHS.map((m) => (
                                                        <option
                                                            key={m.value}
                                                            value={m.value}
                                                        >
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                                {errors.fromMonth && (
                                                    <p
                                                        style={{
                                                            color: "red",
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {errors.fromMonth}
                                                    </p>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                To Month
                                                <span className="text-danger">
                                                    *
                                                </span>
                                            </Form.Label>

                                            <Col sm={8}>
                                                <Form.Select
                                                    value={toMonth}
                                                    onChange={(e) => {
                                                        setToMonth(
                                                            e.target.value
                                                        );

                                                        setErrors((prev) => ({
                                                            ...prev,
                                                            toMonth: "",
                                                        }));
                                                    }}
                                                >
                                                    <option value="">
                                                        Select Month
                                                    </option>

                                                    {MONTHS.map((m) => (
                                                        <option
                                                            key={m.value}
                                                            value={m.value}
                                                        >
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                                {errors.toMonth && (
                                                    <p
                                                        style={{
                                                            color: "red",
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {errors.toMonth}
                                                    </p>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Active
                                            </Form.Label>

                                            <Col
                                                sm={8}
                                                className="d-flex align-items-center gap-2"
                                            >
                                                <div className="leave-toggle">
                                                    <Form.Check
                                                        type="switch"
                                                        id="status-switch"
                                                        checked={isActive}
                                                        onChange={(e) =>
                                                            setIsActive(
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <span
                                                    style={{
                                                        fontSize: "14px",
                                                        color: "#6c757d",
                                                    }}
                                                >
                                                    if Active
                                                </span>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row className="align-items-center">
                                    <Col className="mt-2 d-flex align-items-center justify-content-center">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            style={{
                                                backgroundColor: "#2E5CB8",
                                                borderColor: "#2E5CB8",
                                            }}
                                        >
                                            {editingId
                                                ? "Update"
                                                : "Submit"}
                                        </Button>

                                        <Button
                                            variant="secondary"
                                            type="button"
                                            style={{
                                                marginLeft: "10px",
                                            }}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col
                    xl={12}
                    lg={12}
                    md={12}
                    xs={12}
                >
                    <Card>
                        <Card.Body>
                            <Row className="mb-3 d-flex justify-content-end align-items-center g-2">
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by Cycle Name..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(
                                                e.target.value
                                            );

                                            setCurrentPage(1);
                                        }}
                                    />
                                </Col>

                                <Col md={2}>
                                    <Form.Select
                                        value={sortOption}
                                        onChange={(e) => {
                                            setSortOption(
                                                e.target.value
                                            );

                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="latest">
                                            Latest First
                                        </option>

                                        <option value="nameAZ">
                                            Name A→Z
                                        </option>
                                    </Form.Select>
                                </Col>

                                <Col md={2}>
                                    <Form.Select
                                        value={filterActive}
                                        onChange={(e) => {
                                            setFilterActive(
                                                e.target.value
                                            );

                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="">
                                            All
                                        </option>

                                        <option value="true">
                                            Active
                                        </option>

                                        <option value="false">
                                            Inactive
                                        </option>
                                    </Form.Select>
                                </Col>

                                <Col md="auto">
                                    <i
                                        className="bi bi-download"
                                        title="Export CSV"
                                        style={{
                                            fontSize: "1.3rem",
                                            cursor: "pointer",
                                            color: "#2E5CB8",
                                        }}
                                        onClick={exportToCSV}
                                    />
                                </Col>
                            </Row>

                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            Sr.No
                                        </th>

                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            Cycle Name
                                        </th>

                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            From Month
                                        </th>

                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            To Month
                                        </th>

                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            Status
                                        </th>

                                        <th
                                            style={{
                                                backgroundColor:
                                                    "#2E5CB8",
                                                color: "white",
                                            }}
                                        >
                                            Action
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {paginated.length > 0 ? (
                                        paginated.map(
                                            (cycle, index) => (
                                                <tr key={cycle.id}>
                                                    <td>
                                                        {(currentPage -
                                                            1) *
                                                            ITEMS_PER_PAGE +
                                                            index +
                                                            1}
                                                    </td>

                                                    <td>
                                                        {
                                                            cycle.cycleName
                                                        }
                                                    </td>

                                                    <td>
                                                        {getMonthLabel(
                                                            cycle.fromMonth
                                                        )}
                                                    </td>

                                                    <td>
                                                        {getMonthLabel(
                                                            cycle.toMonth
                                                        )}
                                                    </td>

                                                    <td>
                                                        {cycle.isActive
                                                            ? "Active"
                                                            : "Inactive"}
                                                    </td>

                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-secondary"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    cycle
                                                                )
                                                            }
                                                            type="button"
                                                            style={{
                                                                marginBottom:
                                                                    "5px",
                                                                padding:
                                                                    "5px 10px",
                                                            }}
                                                        >
                                                            <i className="bi bi-pencil-fill me-2" />

                                                            Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan="6"
                                                className="text-center"
                                            >
                                                No leave cycles found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>

                            <div className="d-flex justify-content-end">
                                <Pagination>
                                    <Pagination.Prev
                                        onClick={() =>
                                            setCurrentPage(
                                                (p) => p - 1
                                            )
                                        }
                                        disabled={
                                            currentPage === 1
                                        }
                                    />

                                    {[
                                        ...Array(totalPages).keys(),
                                    ].map((number) => (
                                        <Pagination.Item
                                            key={number + 1}
                                            active={
                                                number + 1 ===
                                                currentPage
                                            }
                                            onClick={() =>
                                                setCurrentPage(
                                                    number + 1
                                                )
                                            }
                                        >
                                            {number + 1}
                                        </Pagination.Item>
                                    ))}

                                    <Pagination.Next
                                        onClick={() =>
                                            setCurrentPage(
                                                (p) => p + 1
                                            )
                                        }
                                        disabled={
                                            currentPage ===
                                                totalPages ||
                                            totalPages === 0
                                        }
                                    />
                                </Pagination>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LeaveCycle;
